import type { AuthIdentity, Paginated, SecurityEvent, SessionAccount } from '@luacentrix/contracts';
import type {
  AllowlistEntry,
  AllowlistSource,
  AllowlistTrustTier,
  BlockPageConfig,
  DetectionSource,
  IpBlockRecord,
  IpBlockStatus,
  IpFamily,
  IpMatch,
  NotificationKind,
  NotificationReadStatus,
  RestrictionKind,
  SecurityAdminAction,
  SecurityNotificationRecord,
  SecurityPolicy,
} from '@luacentrix/security';
import type { EpochMillis } from '@luacentrix/shared';

export type SecurityEventsPage = Paginated<SecurityEvent>;

export type SecurityBlocksPage = Paginated<IpBlockRecord>;

export type SecurityActionsPage = Paginated<SecurityAdminAction>;

export type SecurityNotificationsPage = Paginated<SecurityNotificationRecord>;

export const SECURITY_STATUS_STATES = ['active', 'expired', 'revoked', 'unknown'] as const;

export const SECURITY_NOTIFICATION_KINDS = [
  'threat_detected',
  'auto_blocked',
  'auth_anomaly',
  'api_abuse_threshold',
  'config_changed',
] as const satisfies readonly NotificationKind[];

export const SECURITY_NOTIFICATION_STATUSES = ['unread', 'read', 'dismissed'] as const;

export const SECURITY_PATHS = {
  status: '/api/v1/security/status',
  events: '/api/v1/security/events',
  blocks: '/api/v1/security/blocks',
  allowlist: '/api/v1/security/allowlist',
  ip: '/api/v1/security/ip',
  stats: '/api/v1/security/stats',
  policy: '/api/v1/security/policy',
  preview: '/api/v1/security/block-page/preview',
  actions: '/api/v1/security/actions',
  notifications: '/api/v1/security/notifications',
  session: '/api/v1/account/session',
} as const;

export const AUTH_PATHS = {
  discordStart: '/auth/discord/start',
  discordCallback: '/auth/discord/callback',
  logout: '/auth/logout',
} as const;

export const DISCORD_AVATAR_CDN_ORIGIN = 'https://cdn.discordapp.com';

export type SecurityStatusState = (typeof SECURITY_STATUS_STATES)[number];

export interface SecurityStatusQuery {
  ref: string;
}

export interface SecurityStatusResponse {
  reference: string;
  restricted: boolean;
  status: SecurityStatusState;
  kind: RestrictionKind | null;
  expires_at: EpochMillis | null;
  reason_text: string | null;
  block_page: BlockPageConfig | null;
}

export interface ListSecurityEventsQuery {
  limit?: number | undefined;
  cursor?: string | undefined;
  severity?: SecurityEvent['severity'] | undefined;
  type?: SecurityEvent['type'] | undefined;
}

export interface ListSecurityBlocksQuery {
  limit?: number | undefined;
  cursor?: string | undefined;
  status?: IpBlockStatus | undefined;
  kind?: RestrictionKind | undefined;
  search?: string | undefined;
}

export interface ListSecurityActionsQuery {
  limit?: number | undefined;
  cursor?: string | undefined;
  target_id?: string | undefined;
}

export interface SecurityIpQueryQuery {
  address: string;
}

export interface SecurityIpQueryResponse {
  address: string;
  family: IpFamily;
  blocked: boolean;
  block: IpBlockRecord | null;
  allowlisted: boolean;
  allowlist: AllowlistEntry | null;
}

export interface SecurityStatsResponse {
  total_blocked: number;
  active_temporary: number;
  active_permanent: number;
  expiring_within_ms: number;
}

export interface CreateSecurityBlockData {
  match: IpMatch;
  kind: RestrictionKind;
  reason_code: string;
  reason_text: string;
  detection_source: DetectionSource;
  expires_at?: EpochMillis | undefined;
  notes?: string | null;
}

export interface RevokeSecurityBlockData {
  reason: string;
}

export interface ExtendSecurityBlockData {
  expires_at: EpochMillis;
  reason: string;
}

export interface AddSecurityAllowlistData {
  match: IpMatch;
  label: string;
  trust_tier: AllowlistTrustTier;
  source: AllowlistSource;
  expires_at?: EpochMillis | null;
  notes?: string | null;
}

export interface RemoveSecurityAllowlistData {
  reason: string;
}

export interface SecurityPolicyResponse {
  policy: SecurityPolicy;
}

export interface UpdateSecurityPolicyData {
  policy: SecurityPolicy;
}

export interface SecurityBlockPagePreviewData {
  block_page: BlockPageConfig;
  kind: RestrictionKind;
  expires_at?: EpochMillis | null;
  reference?: string | undefined;
}

export interface SecurityBlockPagePreviewResponse {
  block_page: BlockPageConfig;
  kind: RestrictionKind;
  reference: string;
  expires_at: EpochMillis | null;
}

export interface SecurityAllowlistResponse {
  items: AllowlistEntry[];
}

export interface AccountSessionResponse {
  identity: AuthIdentity;
  account?: SessionAccount | null;
}

export interface SignOutResponse {
  revoked: boolean;
}

export interface ListSecurityNotificationsQuery {
  limit?: number | undefined;
  cursor?: string | undefined;
  status?: NotificationReadStatus | undefined;
  kind?: NotificationKind | undefined;
  severity?: SecurityEvent['severity'] | undefined;
}

export interface SecurityNotificationsResponse extends SecurityNotificationsPage {
  unread_count: number;
}
